import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useEnqueueStacks } from '@avangard/utils/hooks';
import { useNavigate } from '@avangard/utils/routing';
import { Button } from '@avangard/ui/core';
import { breakpoints } from '@avangard/ui/utils';

import { routes } from '@config/routes';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { GetCurrentUserQuery } from '@modules/auth/graphql';
import { UpdateSchoolRequest } from '@modules/schools/requests';
import { UpdateSchoolMutation } from '@modules/schools/graphql';
import { RequisitesEditFormGroup } from './requisites-edit-form-group';

import type { SchoolEntity } from '@modules/schools/entities';
import type {
    UpdateSchoolMutationType,
    UpdateSchoolMutationVariables,
} from '@modules/types/graphql';

type RequisitesEditFormProps = {
    school: SchoolEntity;
};

const StyledForm = styled(Form)`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
`;

const Footer = styled.div`
    margin-block-start: 40px;
    padding-block-end: 60px;

    ${breakpoints.down('xs')} {
        width: 100%;
        padding-block-end: 40px;
        margin-block-start: 20px;

        button {
            width: 100%;
        }
    }
`;

const RequisitesEditForm = (props: RequisitesEditFormProps): React.ReactElement => {
    const { school } = props;

    const { t } = useTranslation(['common', 'errors']);
    const navigate = useNavigate();

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [updateSchool] = useMutation<UpdateSchoolMutationType, UpdateSchoolMutationVariables>(
        UpdateSchoolMutation,
    );

    const initialValues = {
        fullName: school.fullName,
        requisites: school.requisites,
    };

    return (
        <ExtendedFormik
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={yup.object({
                fullName: yup.string().required(t('errors:validations.required') ?? ''),
                requisites: yup.object({
                    inn: yup
                        .string()
                        .required(t('errors:validations.required') ?? '')
                        .matches(/^\d{10}$/, t('errors:requisites.inn') ?? ''),
                    kpp: yup
                        .string()
                        .required(t('errors:validations.required') ?? '')
                        .matches(/^\d{9}$/, t('errors:requisites.kpp') ?? ''),
                    ogrn: yup
                        .string()
                        .required(t('errors:validations.required') ?? '')
                        .matches(/^\d{13}$/, t('errors:requisites.ogrn') ?? ''),
                    address: yup.string().required(t('errors:validations.required') ?? ''),
                    responsibleName: yup.string().required(t('errors:validations.required') ?? ''),
                    responsiblePhone: yup.string().required(t('errors:validations.required') ?? ''),
                    responsibleEmail: yup.string().required(t('errors:validations.required') ?? ''),
                    signerName: yup.string().required(t('errors:validations.required') ?? ''),
                    signerPosition: yup.string().required(t('errors:validations.required') ?? ''),
                    signerPositionGenitive: yup
                        .string()
                        .required(t('errors:validations.required') ?? ''),
                    signerNameGenitive: yup
                        .string()
                        .required(t('errors:validations.required') ?? ''),
                    licence: yup.string().required(t('errors:validations.required') ?? ''),
                }),
            })}
            initialValues={initialValues}
            onSubmit={async values => {
                try {
                    const item = new UpdateSchoolRequest(values);

                    const { data: updateSchoolData } = await updateSchool({
                        variables: { id: school.id, item },
                        refetchQueries: [{ query: GetCurrentUserQuery }],
                        awaitRefetchQueries: true,
                    });

                    if (updateSchoolData?.updateSchool?.requisites) {
                        enqueueSuccess(t('common:blocks.contract.success_update'));

                        await navigate(routes.platform.path);
                    } else {
                        enqueueError(t('common:blocks.contract.failed_update'));
                    }
                } catch (e) {
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { isSubmitting } = formikProps;

                return (
                    <StyledForm>
                        <RequisitesEditFormGroup />

                        <Footer>
                            <Button type='submit' loading={isSubmitting}>
                                {t('common:actions.save')}
                            </Button>
                        </Footer>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { RequisitesEditForm };
